import { DateTime } from "luxon";
import { Signal, SignalType, ThreadOnTrackSignal } from "@types";
import { env } from "@config";
import { groupBy } from "./groupBy";

export const isSignal = (signal: unknown): signal is Signal => {
  return !!signal && !!(signal as Signal).eventId;
};

export const getSignalsListHeaderLabel = (dateString: string): string => {
  const currentDate = DateTime.local();
  const signalDate = DateTime.fromISO(dateString).toLocal();
  const isToday = signalDate.hasSame(currentDate, "day");
  const isYesterday = signalDate.hasSame(currentDate.minus({ days: 1 }), "day");

  if (isToday) {
    return "Today";
  }

  if (isYesterday) {
    return "Yesterday";
  }

  return signalDate.toFormat("d LLL");
};

export const getSignalTime = (signal?: Signal): string => {
  const isToday = DateTime.fromISO(signal?.eventDate).toLocal().hasSame(DateTime.local(), "day");
  const isThisYear = DateTime.fromISO(signal?.eventDate)
    .toLocal()
    .hasSame(DateTime.local(), "year");

  if (isToday) {
    return DateTime.fromISO(signal?.eventDate).toLocal().toFormat("t").toLowerCase();
  }

  if (isThisYear) {
    return DateTime.fromISO(signal?.eventDate).toLocal().toFormat("LLL d");
  }

  return DateTime.fromISO(signal?.eventDate).toLocal().toFormat("yyyy");
};

export const getSignalFullDate = (dateString: string): string =>
  `${DateTime.fromISO(dateString).toLocal().toFormat("LLL d, yyyy")}, ${DateTime.fromISO(dateString).toLocal().toFormat("t").toLowerCase()}`;

export const getSignalTitle = (signal?: Signal): string => {
  switch (signal?.eventName) {
    case SignalType.Welcome: {
      return "Welcome to Substrata";
    }
    case SignalType.WelcomeBack: {
      return "Welcome back to Substrata";
    }
    case SignalType.NewTeamMember: {
      return "New team member joined";
    }
    case SignalType.SentimentDecreased: {
      return "Sentiment changed to negative";
    }
    case SignalType.SentimentIncreased: {
      return "Sentiment changed to positive";
    }
    case SignalType.PointOfContactChanged: {
      return "Point of contact changed";
    }
    case SignalType.ToAdded:
    case SignalType.CcAdded:
    case SignalType.ToThirdPartyAdded:
    case SignalType.CcThirdPartyAdded: {
      const amount = signal.eventMetadata?.added?.length || 0;
      return amount > 1 ? "New participants added" : "Participant added";
    }
    case SignalType.ToRemoved:
    case SignalType.CcRemoved:
    case SignalType.ToThirdPartyRemoved:
    case SignalType.CcThirdPartyRemoved: {
      const amount = signal.eventMetadata?.removed?.length || 0;
      return amount > 1 ? "Participants removed" : "Participant removed";
    }
    case SignalType.EmailOpened: {
      return "Email Opened";
    }
    default:
      return "";
  }
};

export const getSignalEmailSubject = (signal?: Signal): string => {
  switch (signal?.eventName) {
    case SignalType.ToAdded:
    case SignalType.ToRemoved:
    case SignalType.CcAdded:
    case SignalType.CcRemoved:
    case SignalType.ToThirdPartyAdded:
    case SignalType.ToThirdPartyRemoved:
    case SignalType.CcThirdPartyAdded:
    case SignalType.CcThirdPartyRemoved:
    case SignalType.PointOfContactChanged:
    case SignalType.SentimentIncreased:
    case SignalType.SentimentDecreased:
      return signal.eventMetadata.emailSubject;
    case SignalType.EmailOpened:
      // case SignalType.ThreadUpdated:
      return signal.eventMetadata.thread.subject;
    case SignalType.Welcome:
    case SignalType.WelcomeBack:
    case SignalType.NewTeamMember:
    default:
      return "";
  }
};

const knownSignalTypes = new Set(Object.values(SignalType));
// Exclude threadOnTrackUpdated from the signals list
// knownSignalTypes.delete(SignalType.ThreadUpdated);

export const getUpdatedSignalsList = (
  currentSignals: Signal[],
  newSignals: Signal[] | null
): Signal[] => {
  if (!newSignals) {
    return [];
  }

  const currentSignalsIds = new Set(currentSignals.map((signal) => signal.eventId));

  return newSignals
    .filter(
      (signal) => knownSignalTypes.has(signal.eventName) && !currentSignalsIds.has(signal.eventId)
    )
    .map((signal) => {
      if (signal.eventName === SignalType.EmailOpened) {
        return {
          ...signal,
          eventMetadata: {
            ...signal.eventMetadata,
            thread: {
              ...signal.eventMetadata.thread,
              onTrack: null,
            },
          },
        };
      }

      return signal;
    })
    .sort((a, b) => new Date(b.eventDate).getTime() - new Date(a.eventDate).getTime());
};

export const getSignalAnalysisViewLink = (signal: Signal | ThreadOnTrackSignal) => {
  if (!isSignal(signal)) {
    const { latestEngagementUrl } = signal;
    return `${env.accountOrigin}${latestEngagementUrl}`;
  }

  switch (signal.eventName) {
    case SignalType.EmailOpened: {
      const { latestEngagementUrl } = signal.eventMetadata.thread;
      return `${env.accountOrigin}${latestEngagementUrl}`;
    }
    case SignalType.ToAdded:
    case SignalType.ToRemoved:
    case SignalType.CcAdded:
    case SignalType.CcRemoved:
    case SignalType.ToThirdPartyAdded:
    case SignalType.ToThirdPartyRemoved:
    case SignalType.CcThirdPartyAdded:
    case SignalType.CcThirdPartyRemoved:
    case SignalType.PointOfContactChanged:
    case SignalType.SentimentDecreased:
    case SignalType.SentimentIncreased: {
      const { latestEngagementUrl } = signal.eventMetadata;
      return latestEngagementUrl ? `${env.accountOrigin}${latestEngagementUrl}` : "";
    }
    default:
      return "";
  }
};

export const getSignalMessageItemId = (signal: Signal | ThreadOnTrackSignal) => {
  if (!isSignal(signal)) {
    const { messageId } = signal?.theFirstTrackedEmail || {};
    return messageId ? messageId : "";
  }

  switch (signal.eventName) {
    case SignalType.EmailOpened: {
      const { messageId } = signal.eventMetadata?.thread?.theFirstTrackedEmail || {};
      return messageId ? messageId : "";
    }
    case SignalType.ToAdded:
    case SignalType.ToRemoved:
    case SignalType.CcAdded:
    case SignalType.CcRemoved:
    case SignalType.ToThirdPartyAdded:
    case SignalType.ToThirdPartyRemoved:
    case SignalType.CcThirdPartyAdded:
    case SignalType.CcThirdPartyRemoved:
    case SignalType.PointOfContactChanged:
    case SignalType.SentimentDecreased:
    case SignalType.SentimentIncreased: {
      const { platformMessageId } = signal.eventMetadata;
      return platformMessageId ? platformMessageId : "";
    }
    default: {
      return "";
    }
  }
};

export const getSignalParticipants = (signal: Signal | ThreadOnTrackSignal) => {
  if (!isSignal(signal)) {
    const { participants } = signal;
    const groupedParticipants = groupBy(participants, (participant) => `${participant.isUserSide}`);

    return {
      userSide: groupedParticipants["true"] || [],
      prospectSide: groupedParticipants["false"] || [],
    };
  }

  switch (signal.eventName) {
    case SignalType.EmailOpened: {
      const {
        thread: { participants },
      } = signal.eventMetadata;
      const groupedParticipants = groupBy(
        participants,
        (participant) => `${participant.isUserSide}`
      );

      return {
        userSide: groupedParticipants["true"] || [],
        prospectSide: groupedParticipants["false"] || [],
      };
    }

    default: {
      return {
        userSide: [],
        prospectSide: [],
      };
    }
  }
};
